import React from "react";
import { motion } from "framer-motion";
import {
  FaBriefcase,
  FaCalendar,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";

const experiences = [
  {
    company: "Olumi Tech",
    role: "React Native Developer",
    period: "Nov 2025 - Apr 2026",
    location: "Remote",
    description:
      "Worked as a mobile application developer on multiple production apps using React Native CLI, building scalable and user-friendly Android/iOS applications.",
    experienceLetter:
      "https://drive.google.com/drive/folders/1zTAADMmlP4ENvGhp_YX0KRjceIiyWKS9?usp=drive_link",
    projects: [
      {
        name: "Riden App (Passenger & Driver App)",
        details: [
          "Developed passenger and driver mobile applications similar to Uber",
          "Implemented ride booking, real-time location tracking and route display using Google Maps API",
          "Integrated Redux for global state management",
          "Built responsive UI using React Native CLI and TypeScript",
          "Optimized app performance and handled API integrations",
        ],
        techStack: [
          "React Native CLI",
          "TypeScript",
          "Redux",
          "Google Maps API",
        ],
      },
      {
        name: "Sabkobol.com",
        details: [
          "Developed a business-oriented mobile application for service and order management",
          "Implemented location-based features using Google Maps",
          "Managed application state with Redux",
          "Ensured smooth navigation and clean UI across devices",
        ],
        techStack: [
          "React Native CLI",
          "TypeScript",
          "Redux",
          "Google Maps API",
        ],
      },
      {
        name: "Ledgerly App",
        details: [
          "Built a business management and ledger application similar to Zoho Books",
          "Implemented features for managing transactions, accounts, and business records",
          "Used Redux for state management and TypeScript for type safety",
          "Worked on UI, logic, and API integration for financial data",
        ],
        techStack: ["React Native CLI", "TypeScript", "Redux"],
      },
    ],
  },
  {
    company: "Senew Tech",
    role: "React Native Developer",
    period: "Jan 2025 - Nov 2025",
    location: "Onsite",
    description:
      "Developed multiple mobile applications including fitness tracking, task management, and live streaming platforms using React Native.",
    experienceLetter:
      "https://drive.google.com/drive/folders/1v7NUnb_X6vzTitTUWYRVEXpXs_fP-4yM?usp=drive_link",
    projects: [
      {
        name: "Taskify App",
        details: [
          "Developed using React Native (Expo) with MongoDB integration",
          "Built task management features with a clean and intuitive UI",
        ],
        techStack: ["React Native", "MongoDB"],
      },
      {
        name: "RunningTribe",
        details: [
          "Designed and implemented the complete UI/UX using React Native CLI",
          "Focused on responsive design and seamless navigation for a fitness tracking app",
        ],
        techStack: ["React Native CLI", "Firebase", "Maps"],
      },
      {
        name: "Peasomy",
        details: [
          "Built using React Native CLI with full API integration using Redux for state management",
          "Developed both front-end UI/UX and connected real-time data from backend services",
        ],
        techStack: ["React Native CLI", "Redux", "REST APIs"],
      },
      {
        name: "LiveStreaming App",
        details: [
          "Developed using React Native CLI",
          "Agora SDK integration for live streaming",
          "Socket.io integration, Auction/Bidding Management",
        ],
        techStack: ["React Native CLI", "Agora SDK", "Socket.io"],
      },
    ],
  },
  {
    company: "Software Engineer",
    role: "AdRoadster Mobile App/ Website",
    period: "2023 - 2025",
    location: "Project",
    description:
      "A mobile application and website designed to revolutionize billboard advertising through targeted ad placement and real-time campaign management.",
    projects: [
      {
        name: "AdRoadster",
        details: [
          "A mobile application and website designed to revolutionize billboard advertising",
          "Built using React Native Expo and React for the front end",
          "Node.js and MongoDB powering the backend",
        ],
        techStack: ["Expo", "React", "Node.js", "MongoDB"],
      },
    ],
  },
];

const ExperienceCard = ({ experience, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: index * 0.2 }}
    viewport={{ once: true }}
    className="relative"
  >
    {/* Timeline line */}
    {index < experiences.length - 1 && (
      <div className="absolute left-6 top-16 bottom-0 w-px bg-gradient-to-b from-accent/50 to-transparent hidden md:block" />
    )}

    <div className="glass-card relative overflow-hidden group hover:border-accent/30 transition-all duration-500">
      {/* Glow effect */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-[50px] pointer-events-none group-hover:bg-accent/20 transition-all duration-500" />

      <div className="relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent/20 to-neon-purple/20 flex items-center justify-center">
                <FaBriefcase className="text-accent" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">
                  {experience.company}
                </h3>
                <p className="text-accent font-medium">{experience.role}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-col md:items-end gap-2 mt-4 md:mt-0">
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <FaCalendar size={12} />
              <span>{experience.period}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <FaMapMarkerAlt size={12} />
              <span>{experience.location}</span>
            </div>
            {experience.experienceLetter && (
              <a
                href={experience.experienceLetter}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 hover:bg-accent/20 transition-all duration-300 text-accent text-sm font-medium border border-accent/20 hover:border-accent/40"
              >
                <FaExternalLinkAlt size={12} />
                Experience Letter
              </a>
            )}
          </div>
        </div>

        <p className="text-slate-400 text-lg mb-8 leading-relaxed">
          {experience.description}
        </p>

        <div className="space-y-6">
          {experience.projects.map((project, pIndex) => (
            <div
              key={pIndex}
              className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all duration-300"
            >
              <h4 className="text-xl font-bold text-white mb-3">
                {project.name}
              </h4>
              <ul className="space-y-2 mb-4">
                {project.details.map((detail, dIndex) => (
                  <li
                    key={dIndex}
                    className="flex items-start gap-3 text-slate-400"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 flex-shrink-0" />
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-2">
                {project.techStack.map((tech, tIndex) => (
                  <span
                    key={tIndex}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-accent/10 border border-accent/20 text-accent"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </motion.div>
);

export default function Experience() {
  return (
    <div className="min-h-screen pt-32 pb-20">
      <div className="section-container max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            Work <span className="text-accent">Experience</span>
          </h2>
          <p className="text-slate-400 text-xl max-w-2xl mx-auto">
            My professional journey as a React Native Developer
          </p>
        </motion.div>

        <div className="space-y-12">
          {experiences.map((experience, index) => (
            <ExperienceCard key={index} experience={experience} index={index} />
          ))}
        </div>
      </div>
    </div>
  );
}
